// 근무시간 입력 — 부서 팀장이 날짜별로 파트 인원 + 지원 온 다른 부서 인원의 출퇴근·야근·조근(주말·공휴일은 특근)을 입력.
// 저장 형식은 WPF 야근 프로그램과 같다 (서버 overtime-input-api 참고). 바꾼 사람만 저장한다.
// 데스크톱 = 표 직접 편집, 모바일 = 사람 목록 → 탭하면 아래에서 올라오는 입력창(빠른 버튼 위주).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ConfigProvider, DatePicker, Select, Button, Space, Table, Tag, Input, Typography, Modal, Drawer, message, Grid, Empty, Spin,
} from 'antd';
import {
  LeftOutlined, RightOutlined, SaveOutlined, UserAddOutlined, CloseOutlined, CheckSquareOutlined, EditOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import ko_KR from 'antd/es/locale/ko_KR';
import '../styles/Common.css';
import { API_BASE } from '../config/api';

dayjs.locale('ko');

const { Text } = Typography;

const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const WEEKDAY_LABELS = ['월', '화', '수', '목', '금', '토', '일'];

// ── 시간 ──
const times = (from, to) => {
  const out = [];
  for (let m = from; m <= to; m += 30) out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  return out;
};
const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const WORK_STARTS = times(5 * 60, 17 * 60);
const WORK_ENDS = times(9 * 60, 23 * 60 + 30);
const EARLY_STARTS = times(5 * 60, 8 * 60);
const EARLY_ENDS = times(6 * 60, 8 * 60 + 30);
const WEEKEND_TIMES = times(6 * 60, 23 * 60 + 30);

// 빠른 입력 버튼
const WEEKDAY_PRESETS = [
  { label: '정상', main: { start: '08:30', end: '17:30' } },
  { label: '19시', main: { end: '19:00' } },
  { label: '20시', main: { end: '20:00' } },
  { label: '21시', main: { end: '21:00' } },
  { label: '22시', main: { end: '22:00' } },
  { label: '오전반차', main: { start: '13:30', end: '17:30' } },
  { label: '오후반차', main: { start: '08:30', end: '13:30' } },
];
const WEEKEND_PRESETS = [
  { label: '종일 08:30~17:30', main: { start: '08:30', end: '17:30' } },
  { label: '오전 08:30~12:00', main: { start: '08:30', end: '12:00' } },
  { label: '오후 13:00~17:30', main: { start: '13:00', end: '17:30' } },
];
const EARLY_PRESETS = ['07:00', '07:30', '08:00'];

// 인정 시간 (서버와 같은 규칙 — 미리보기용). 반환: { work: 정상(평일), ot: 연장(야근·특근), early: 조근 } 시간
const overlap = (s, e, a, b) => Math.max(0, Math.min(e, b) - Math.max(s, a));
function calc(weekend, r) {
  const out = { work: 0, ot: 0, early: 0, valid: true };
  const m = r.main;
  if (m && m.start && m.end) {
    const s = toMin(m.start); const e = toMin(m.end);
    if (e <= s) out.valid = false;
    else if (weekend) {
      out.ot = Math.max(0, e - s - overlap(s, e, 720, 780) - (e > 1080 ? overlap(s, e, 1050, 1080) : 0)) / 60;
    } else {
      const regEnd = Math.min(e, 1050);
      out.work = regEnd > s ? (regEnd - s - overlap(s, regEnd, 720, 780)) / 60 : 0;
      out.ot = e > 1050 ? (e - 1050 - (e > 1080 ? 30 : 0)) / 60 : 0;
    }
  } else if (m) out.valid = false;
  const ea = r.early;
  if (!weekend && ea) {
    if (!ea.start || !ea.end || toMin(ea.end) <= toMin(ea.start)) out.valid = false;
    else out.early = (toMin(ea.end) - toMin(ea.start)) / 60;
  }
  return out;
}
const extraOf = (weekend, r) => { const c = calc(weekend, r); return c.ot + c.early; };

const fmtH = (v) => (Number.isInteger(v) ? String(v) : v.toFixed(1));
const sameRec = (a, b) => (!a && !b) || (a && b && a.start === b.start && a.end === b.end);
const isDirty = (r, o) => (!o ? !!(r.main || r.early) : false)
  || (!!o && (!sameRec(r.main, o.main) || !sameRec(r.early, o.early) || (r.note || '') !== (o.note || '')));

// ── 공통 입력기: 빠른 버튼 + 시각 선택 + 조근 + 작업 내용 (모바일 입력창·일괄 입력에서 사용) ──
function DayEditor({ weekend, value, onChange, bulk }) {
  const main = value.main;
  const early = value.early;
  const setMain = (patch) => onChange({ ...value, main: patch === null ? null : { start: main?.start || '08:30', end: main?.end || '17:30', ...patch } });
  const presetOn = (p) => main && main.end === p.main.end && (!p.main.start || main.start === p.main.start);
  const big = { size: 'large', style: { width: '100%' } };
  return (
    <div className="oti-editor">
      <div className="oti-ed-label">{weekend ? '특근' : '출퇴근'}</div>
      <div className="oti-chips">
        {(weekend ? WEEKEND_PRESETS : WEEKDAY_PRESETS).map((p) => (
          <button key={p.label} type="button" className={presetOn(p) ? 'on' : ''} onClick={() => setMain(p.main)}>{p.label}</button>
        ))}
        <button type="button" className={!main ? 'on off' : 'off'} onClick={() => setMain(null)}>{bulk ? '기록 삭제' : '없음'}</button>
      </div>
      <div className="oti-ed-times">
        <Select {...big} value={main?.start} placeholder={weekend ? '시작' : '출근'} onChange={(v) => setMain({ start: v })}
          options={(weekend ? WEEKEND_TIMES : WORK_STARTS).map((t) => ({ value: t, label: `${weekend ? '시작' : '출근'} ${t}` }))} />
        <span>~</span>
        <Select {...big} value={main?.end} placeholder={weekend ? '종료' : '퇴근'} onChange={(v) => setMain({ end: v })}
          options={(weekend ? WEEKEND_TIMES : WORK_ENDS).map((t) => ({ value: t, label: `${weekend ? '종료' : '퇴근'} ${t}` }))} />
      </div>

      {!weekend && (
        <>
          <div className="oti-ed-label">조근 <small>(~08:30)</small></div>
          <div className="oti-chips">
            <button type="button" className={!early ? 'on off' : 'off'} onClick={() => onChange({ ...value, early: null })}>없음</button>
            {EARLY_PRESETS.map((t) => (
              <button key={t} type="button" className={early?.start === t && early?.end === '08:30' ? 'on' : ''}
                onClick={() => onChange({ ...value, early: { start: t, end: '08:30' } })}>{t}~</button>
            ))}
          </div>
          {early && (
            <div className="oti-ed-times">
              <Select {...big} value={early.start} onChange={(v) => onChange({ ...value, early: { ...early, start: v } })}
                options={EARLY_STARTS.map((t) => ({ value: t, label: `시작 ${t}` }))} />
              <span>~</span>
              <Select {...big} value={early.end} onChange={(v) => onChange({ ...value, early: { ...early, end: v } })}
                options={EARLY_ENDS.map((t) => ({ value: t, label: `종료 ${t}` }))} />
            </div>
          )}
        </>
      )}

      <div className="oti-ed-label">작업 내용</div>
      <Input size="large" value={value.note} maxLength={300} placeholder={bulk ? '비우면 기존 내용 유지' : '예) 쿠팡, 납품'}
        onChange={(e) => onChange({ ...value, note: e.target.value })} />
    </div>
  );
}

const summaryText = (weekend, r) => {
  const parts = [];
  if (r.main) parts.push(`${r.main.start}~${r.main.end}`);
  if (!weekend && r.early) parts.push(`조근 ${r.early.start}~`);
  return parts.join(' · ');
};

const OvertimeInputPage = () => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === undefined ? window.innerWidth < 768 : !screens.md;

  const [date, setDate] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [parts, setParts] = useState([]);
  const [part, setPart] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [data, setData] = useState(null); // 서버 응답
  const [rows, setRows] = useState([]); // 편집 중인 값
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selectMode, setSelectMode] = useState(false); // 모바일 선택 모드
  const [editNo, setEditNo] = useState(null); // 모바일 입력창 대상
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ main: null, early: null, note: '' });
  const [addOpen, setAddOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [toAdd, setToAdd] = useState([]);
  const [pickDate, setPickDate] = useState(false);

  const weekend = data?.day_type === 'WEEKEND';
  const original = useMemo(() => new Map((data?.rows || []).map((r) => [r.employee_no, r])), [data]);
  const dirtyRows = rows.filter((r) => isDirty(r, original.get(r.employee_no)));

  useEffect(() => {
    api.get('/api/overtime-input/parts').then(({ data: res }) => {
      setParts(res.parts || []);
      setCanEdit(!!res.can_edit);
      if (res.parts?.length) setPart((p) => p || res.parts[0].part);
    }).catch((e) => message.error(e.response?.data?.message || '부서 목록을 불러오지 못했습니다.'));
  }, []);

  const fetchDay = useCallback(async () => {
    if (!part) return;
    setLoading(true);
    try {
      const { data: res } = await api.get('/api/overtime-input', { params: { date, part } });
      if (res.success) {
        setData(res);
        setRows(res.rows.map((r) => ({ ...r })));
        setCanEdit(!!res.can_edit);
        setSelected([]);
      }
    } catch (e) {
      setData(null);
      setRows([]);
      message.error(e.response?.data?.message || '불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [date, part]);

  useEffect(() => { fetchDay(); }, [fetchDay]);

  // 저장 안 한 변경이 있으면 날짜/부서 바꾸기 전에 확인
  const guard = (fn) => {
    if (!dirtyRows.length) { fn(); return; }
    Modal.confirm({
      title: '저장하지 않은 변경이 있습니다',
      content: `${dirtyRows.length}명의 입력이 저장되지 않았습니다. 버리고 이동할까요?`,
      okText: '버리고 이동', cancelText: '취소', onOk: fn,
    });
  };
  const moveDay = (n) => guard(() => setDate(dayjs(date).add(n, 'day').format('YYYY-MM-DD')));

  const update = (no, patch) => setRows((list) => list.map((r) => (r.employee_no === no ? { ...r, ...patch } : r)));

  const openBulk = () => {
    if (!selected.length) { message.info('입력할 인원을 먼저 선택하세요.'); return; }
    setBulk({ main: weekend ? { start: '08:30', end: '17:30' } : { start: '08:30', end: '21:00' }, early: null, note: '' });
    setBulkOpen(true);
  };
  const applyBulk = () => {
    if (!calc(weekend, bulk).valid) { message.error('시간이 올바르지 않습니다.'); return; }
    setRows((list) => list.map((r) => (selected.includes(r.employee_no)
      ? { ...r, main: bulk.main, early: weekend ? null : bulk.early, note: bulk.note ? bulk.note : r.note }
      : r)));
    message.success(`${selected.length}명에게 적용했습니다. 저장을 눌러야 반영됩니다.`);
    setBulkOpen(false);
    setSelected([]);
    setSelectMode(false);
  };

  const save = async () => {
    if (!dirtyRows.length) return;
    const bad = dirtyRows.find((r) => !calc(weekend, r).valid);
    if (bad) { message.error(`${bad.name}: 시간이 올바르지 않습니다.`); return; }
    setSaving(true);
    try {
      await api.put('/api/overtime-input', {
        date, part,
        rows: dirtyRows.map((r) => ({ employee_no: r.employee_no, main: r.main, early: weekend ? null : r.early, note: r.note || '' })),
      });
      message.success(`${dirtyRows.length}명 저장했습니다.`);
      fetchDay();
    } catch (e) {
      message.error(e.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const openAdd = async () => {
    setAddOpen(true);
    setToAdd([]);
    try {
      const { data: res } = await api.get('/api/overtime-input/candidates', { params: { part } });
      setCandidates(res.employees || []);
    } catch (e) {
      message.error(e.response?.data?.message || '인원 목록을 불러오지 못했습니다.');
    }
  };

  const addSupporters = () => {
    const have = new Set(rows.map((r) => r.employee_no));
    const add = candidates.filter((c) => toAdd.includes(c.employee_no) && !have.has(c.employee_no)).map((c) => ({
      ...c, is_support: true, main: null, early: null, note: '', elsewhere: [],
    }));
    setRows((list) => [...list, ...add]);
    setSelected((s) => [...s, ...add.map((a) => a.employee_no)]);
    if (isMobile && add.length) setSelectMode(true);
    setAddOpen(false);
  };

  const removeNewSupporter = (no) => {
    setRows((list) => list.filter((r) => r.employee_no !== no));
    setSelected((s) => s.filter((x) => x !== no));
  };

  const toggleSel = (no) => setSelected((s) => (s.includes(no) ? s.filter((x) => x !== no) : [...s, no]));

  // ── 표시 조각 ──
  const nameTags = (r) => (
    <>
      {r.is_support && <Tag color="purple">지원 · {r.part_name}</Tag>}
      {r.elsewhere?.map((x, i) => <Tag key={i}>{x.part_name} 지원 {x.early ? '조근 ' : ''}{fmtH(x.hours)}h</Tag>)}
      {isDirty(r, original.get(r.employee_no)) && <Tag color="gold">변경</Tag>}
    </>
  );

  const hoursBadge = (r) => {
    const c = calc(weekend, r);
    const extra = c.ot + c.early;
    if (!c.valid) return <Text type="danger">확인</Text>;
    if (extra) return <b className="oti-extra">+{fmtH(extra)}h</b>;
    if (r.main) return <Text type="secondary">{fmtH(c.work)}h</Text>;
    return <Text type="secondary">-</Text>;
  };

  // ── 데스크톱 표 ──
  const timeSelect = (value, options, onChange, placeholder, width = 84) => (
    <Select
      size="small" value={value} onChange={onChange} placeholder={placeholder} allowClear disabled={!canEdit}
      style={{ width }} options={options.map((t) => ({ value: t, label: t }))} popupMatchSelectWidth={false}
    />
  );
  const setMainField = (r, field, v) => {
    if (!v && field === 'end') { update(r.employee_no, { main: null }); return; }
    const base = r.main || { start: '08:30', end: weekend ? '17:30' : '17:30' };
    update(r.employee_no, { main: { ...base, [field]: v || '08:30' } });
  };
  const setEarlyField = (r, field, v) => {
    if (!v && field === 'start') { update(r.employee_no, { early: null }); return; }
    const base = r.early || { start: '07:00', end: '08:30' };
    update(r.employee_no, { early: { ...base, [field]: v || '08:30' } });
  };

  const columns = [
    {
      title: '이름', key: 'name',
      render: (_, r) => (
        <div className="oti-name">
          <b>{r.name}</b>{r.rank && <Text type="secondary" style={{ fontSize: 12 }}>{r.rank}</Text>}
          {nameTags(r)}
          {!original.has(r.employee_no) && <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => removeNewSupporter(r.employee_no)} aria-label="빼기" />}
        </div>
      ),
    },
    {
      title: weekend ? '특근 시작 ~ 종료' : '출근 ~ 퇴근', key: 'main', width: 210,
      render: (_, r) => (
        <Space size={4}>
          {timeSelect(r.main?.start, weekend ? WEEKEND_TIMES : WORK_STARTS, (v) => setMainField(r, 'start', v), weekend ? '시작' : '출근')}
          <span>~</span>
          {timeSelect(r.main?.end, weekend ? WEEKEND_TIMES : WORK_ENDS, (v) => setMainField(r, 'end', v), weekend ? '종료' : '퇴근')}
        </Space>
      ),
    },
    ...(weekend ? [] : [{
      title: '조근', key: 'early', width: 210,
      render: (_, r) => (
        <Space size={4}>
          {timeSelect(r.early?.start, EARLY_STARTS, (v) => setEarlyField(r, 'start', v), '없음')}
          <span>~</span>
          {timeSelect(r.early?.end, EARLY_ENDS, (v) => setEarlyField(r, 'end', v), '08:30')}
        </Space>
      ),
    }]),
    {
      title: '작업 내용', key: 'note', width: 220,
      render: (_, r) => <Input size="small" value={r.note} maxLength={300} disabled={!canEdit} onChange={(e) => update(r.employee_no, { note: e.target.value })} />,
    },
    { title: weekend ? '특근' : '연장', key: 'hours', width: 70, align: 'right', render: (_, r) => hoursBadge(r) },
  ];

  const totalExtra = rows.reduce((s, r) => s + extraOf(weekend, r), 0);
  const workers = rows.filter((r) => r.main || (!weekend && r.early)).length;
  const d = dayjs(date);
  const dayTag = `${WEEKDAY_LABELS[(d.day() + 6) % 7]}${data?.holiday ? ` · ${data.holiday}` : ''}${weekend ? ' · 특근' : ''}`;

  const editRow = rows.find((r) => r.employee_no === editNo);
  const editIdx = rows.findIndex((r) => r.employee_no === editNo);

  const addModal = (
    <Modal
      open={addOpen} title={`${data?.part_name || ''} 지원 인원 추가`} onCancel={() => setAddOpen(false)} onOk={addSupporters}
      okText="추가" cancelText="취소" okButtonProps={{ disabled: !toAdd.length }}
    >
      <Select
        mode="multiple" showSearch style={{ width: '100%' }} size={isMobile ? 'large' : 'middle'} placeholder="이름 또는 부서로 검색"
        value={toAdd} onChange={setToAdd} optionFilterProp="search"
        options={candidates.filter((c) => !rows.some((r) => r.employee_no === c.employee_no)).map((c) => ({
          value: c.employee_no, label: `${c.name} (${c.part_name})`, search: `${c.name} ${c.part_name} ${c.part}`,
        }))}
      />
      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
        추가한 인원은 선택된 상태로 목록 아래에 들어갑니다. 일괄 입력으로 시간을 넣고 저장하세요.
      </Text>
    </Modal>
  );

  const bulkEditor = (
    <DayEditor weekend={weekend} value={bulk} onChange={setBulk} bulk />
  );

  // ── 모바일 ──
  if (isMobile) {
    return (
      <ConfigProvider locale={ko_KR}>
        <div className="oti-m">
          <div className="oti-m-top">
            <div className="oti-m-daynav">
              <button type="button" onClick={() => moveDay(-1)} aria-label="전날"><LeftOutlined /></button>
              <button type="button" className="oti-m-date" onClick={() => setPickDate(true)}>
                <b>{d.format('M월 D일')}</b> <span className={weekend ? 'is-weekend' : ''}>({dayTag})</span>
              </button>
              <button type="button" onClick={() => moveDay(1)} aria-label="다음날"><RightOutlined /></button>
              <DatePicker
                open={pickDate} onOpenChange={setPickDate} value={d} allowClear={false} inputReadOnly
                className="oti-m-hidden-picker" getPopupContainer={(el) => el.parentNode}
                onChange={(v) => { setPickDate(false); if (v) guard(() => setDate(v.format('YYYY-MM-DD'))); }}
              />
            </div>
            <div className="oti-m-sub">
              <Select
                value={part} onChange={(v) => guard(() => setPart(v))} placeholder="부서" style={{ flex: 1 }}
                options={parts.map((p) => ({ value: p.part, label: p.part_name }))}
              />
              {data && <span className="oti-m-stat">{workers}/{rows.length}명 · +{fmtH(totalExtra)}h</span>}
            </div>
          </div>

          {parts.length === 0 && !loading && (
            <Empty style={{ marginTop: 40 }} description="입력할 수 있는 부서가 없습니다. 관리자에게 '사용자 부서' 지정을 요청하세요." />
          )}

          <Spin spinning={loading}>
            <ul className="oti-m-list">
              {rows.map((r) => {
                const sel = selected.includes(r.employee_no);
                return (
                  <li key={r.employee_no} className={`${sel ? 'is-selected' : ''}${r.is_support ? ' is-support' : ''}`}>
                    <button
                      type="button" className="oti-m-row" disabled={!canEdit}
                      onClick={() => (selectMode ? toggleSel(r.employee_no) : setEditNo(r.employee_no))}
                    >
                      {selectMode && <span className={`oti-m-check${sel ? ' on' : ''}`} aria-hidden="true" />}
                      <span className="oti-m-who">
                        <b>{r.name}</b>
                        <span className="oti-m-tags">{nameTags(r)}</span>
                        <small>{summaryText(weekend, r) || '기록 없음'}</small>
                      </span>
                      <span className="oti-m-hours">{hoursBadge(r)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Spin>

          {data && canEdit && (
            <div className="oti-m-bar">
              {selectMode ? (
                <>
                  <Button size="large" onClick={() => { setSelectMode(false); setSelected([]); }}>취소</Button>
                  <Button size="large" onClick={() => setSelected(selected.length === rows.length ? [] : rows.map((r) => r.employee_no))}>
                    {selected.length === rows.length ? '전체 해제' : '전체'}
                  </Button>
                  <Button size="large" type="primary" disabled={!selected.length} onClick={openBulk} style={{ flex: 1 }}>
                    {selected.length}명 일괄 입력
                  </Button>
                </>
              ) : (
                <>
                  <Button size="large" icon={<UserAddOutlined />} onClick={openAdd} aria-label="지원 인원 추가">지원</Button>
                  <Button size="large" icon={<CheckSquareOutlined />} onClick={() => setSelectMode(true)}>선택</Button>
                  <Button size="large" type="primary" icon={<SaveOutlined />} loading={saving} disabled={!dirtyRows.length} onClick={save} style={{ flex: 1 }}>
                    저장{dirtyRows.length ? ` (${dirtyRows.length})` : ''}
                  </Button>
                </>
              )}
            </div>
          )}

          {/* 한 사람 입력창 */}
          <Drawer
            placement="bottom" open={!!editRow} onClose={() => setEditNo(null)} height="auto" rootClassName="oti-sheet"
            title={editRow && (
              <div className="oti-sheet-title">
                <b>{editRow.name}</b>
                <span className="oti-m-tags">{editRow.is_support && <Tag color="purple">지원 · {editRow.part_name}</Tag>}</span>
                <span className="oti-sheet-hours">{hoursBadge(editRow)}</span>
              </div>
            )}
            extra={editRow && !original.has(editRow.employee_no) && (
              <Button type="text" danger onClick={() => { removeNewSupporter(editRow.employee_no); setEditNo(null); }}>빼기</Button>
            )}
          >
            {editRow && (
              <>
                <DayEditor weekend={weekend} value={editRow} onChange={(v) => update(editRow.employee_no, { main: v.main, early: v.early, note: v.note })} />
                <div className="oti-sheet-nav">
                  <Button size="large" disabled={editIdx <= 0} onClick={() => setEditNo(rows[editIdx - 1].employee_no)} icon={<LeftOutlined />}>이전</Button>
                  <Button size="large" type="primary" onClick={() => setEditNo(null)} style={{ flex: 1 }}>완료</Button>
                  <Button size="large" disabled={editIdx >= rows.length - 1} onClick={() => setEditNo(rows[editIdx + 1].employee_no)}>다음 <RightOutlined /></Button>
                </div>
              </>
            )}
          </Drawer>

          {/* 일괄 입력창 */}
          <Drawer
            placement="bottom" open={bulkOpen} onClose={() => setBulkOpen(false)} height="auto" rootClassName="oti-sheet"
            title={<div className="oti-sheet-title"><b>{selected.length}명 일괄 입력</b></div>}
          >
            {bulkEditor}
            <div className="oti-sheet-nav">
              <Button size="large" onClick={() => setBulkOpen(false)}>취소</Button>
              <Button size="large" type="primary" onClick={applyBulk} style={{ flex: 1 }}>{selected.length}명에게 적용</Button>
            </div>
          </Drawer>

          {addModal}
        </div>
      </ConfigProvider>
    );
  }

  // ── 데스크톱 ──
  return (
    <ConfigProvider locale={ko_KR}>
      <div className="content-wrapper oti">
        <Space wrap style={{ marginBottom: 12 }}>
          <Button icon={<LeftOutlined />} onClick={() => moveDay(-1)} aria-label="전날" />
          <DatePicker value={d} allowClear={false} onChange={(v) => v && guard(() => setDate(v.format('YYYY-MM-DD')))} />
          <Button icon={<RightOutlined />} onClick={() => moveDay(1)} aria-label="다음날" />
          <Tag color={weekend ? 'volcano' : 'blue'} style={{ marginInlineEnd: 0 }}>{dayTag}요일</Tag>
          <Select
            value={part} onChange={(v) => guard(() => setPart(v))} style={{ width: 150 }} placeholder="부서"
            options={parts.map((p) => ({ value: p.part, label: p.part_name }))}
          />
        </Space>

        {parts.length === 0 && !loading && (
          <Empty description="입력할 수 있는 부서가 없습니다. 관리자에게 '사용자 부서' 지정을 요청하세요." />
        )}

        {data && (
          <>
            <Spin spinning={loading}>
              <Table
                rowKey="employee_no" size="small" pagination={false} columns={columns} dataSource={rows}
                rowSelection={canEdit ? { selectedRowKeys: selected, onChange: setSelected } : undefined}
                rowClassName={(r) => (r.is_support ? 'oti-support-row' : '')}
              />
            </Spin>

            <div className="oti-footer">
              <Text type="secondary">
                {data.part_name} · {rows.length}명 중 {workers}명 · {weekend ? '특근' : '연장'} 합계 {fmtH(totalExtra)}시간
              </Text>
              {canEdit && (
                <Space wrap>
                  <Button icon={<EditOutlined />} disabled={!selected.length} onClick={openBulk}>
                    선택 {selected.length}명 일괄 입력
                  </Button>
                  <Button icon={<UserAddOutlined />} onClick={openAdd}>지원 인원 추가</Button>
                  <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={!dirtyRows.length} onClick={save}>
                    저장{dirtyRows.length ? ` (${dirtyRows.length}명)` : ''}
                  </Button>
                </Space>
              )}
            </div>
          </>
        )}

        <Modal
          open={bulkOpen} title={`선택 ${selected.length}명 일괄 입력`} onCancel={() => setBulkOpen(false)} onOk={applyBulk}
          okText={`${selected.length}명에게 적용`} cancelText="취소" width={480}
        >
          {bulkEditor}
        </Modal>
        {addModal}
      </div>
    </ConfigProvider>
  );
};

export default OvertimeInputPage;
